import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Upload, BarChart2, Library, Radio, DollarSign, Trash2, Link as LinkIcon, Mail, MessageCircle, Crown, Inbox, Bell, Send as SendIcon, Plus as PlusIcon, FileEdit, Music2, Sparkles } from "lucide-react";
import StoryComposer from "../components/StoryComposer";
import StudioMobileFab from "../components/StudioMobileFab";
import { toast } from "sonner";
import VideoRecorder from "../components/VideoRecorder";
import SocialImport from "../components/SocialImport";
import DeleteAccountCard from "../components/DeleteAccountCard";
import GoLiveOBSPanel from "../components/GoLiveOBSPanel";
import EmailBlastPanel from "../components/EmailBlastPanel";
import SmsBlastPanel from "../components/SmsBlastPanel";
import FanClubPanel from "../components/FanClubPanel";
import WaiverModal from "../components/WaiverModal";

function TabButton({ active, onClick, icon: Icon, children, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`shrink-0 flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2.5 rounded-lg text-[11px] sm:text-sm font-semibold transition ${
        active ? "bg-tv-accent text-black" : "text-white/70 hover:bg-white/5"
      }`}
    >
      <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> {children}
    </button>
  );
}

export default function Studio() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => searchParams.get("tab") || "overview");
  const [analytics, setAnalytics] = useState(null);
  const [myVideos, setMyVideos] = useState([]);
  const [cats, setCats] = useState([]);

  // Sync tab when URL query changes (e.g. Go Live nav from header)
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && t !== tab) setTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    api.get("/creator/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    api.get(`/videos?creator_id=${user.user_id}&limit=100`).then((r) => setMyVideos(r.data));
    api.get("/categories").then((r) => setCats(r.data));
  }, [user.user_id]);

  return (
    <div data-testid="studio-page" className="pt-20 lg:pt-32 pb-28 lg:pb-20 px-4 sm:px-8 md:px-12 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-6 sm:mb-8 flex-wrap gap-3 sm:gap-4">
        <div>
          <div className="text-[11px] sm:text-xs uppercase tracking-[0.3em] text-tv-accent font-semibold">Creator Studio</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-white tracking-tighter mt-1 sm:mt-2 leading-none">{user.name}</h1>
          <div className="text-white/60 mt-1 text-xs sm:text-sm uppercase tracking-wider">{user.role.replace("_", " ")}</div>
        </div>
        {user.role === "premium_creator" || user.role === "admin" ? (
          <div className="flex flex-wrap items-center gap-2">
            <button data-testid="studio-go-live-btn" onClick={() => nav("/studio/live")} className="bg-tv-live hover:bg-red-600 text-white font-bold px-5 sm:px-6 py-2.5 sm:py-3 rounded-full flex items-center gap-2 text-sm sm:text-base">
              <Radio className="w-4 h-4" /> Director's Mode
            </button>
            <button
              data-testid="studio-vod-bg-remove-btn"
              onClick={() => nav("/studio/vod-bg-remove")}
              className="bg-white/10 hover:bg-white/20 text-white font-bold px-4 sm:px-5 py-2.5 sm:py-3 rounded-full flex items-center gap-2 text-xs sm:text-sm border border-white/10"
              title="Remove the background from a pre-recorded video"
            >
              <Sparkles className="w-4 h-4 text-tv-accent" /> Remove BG from Video
            </button>
          </div>
        ) : (
          <Link to="/subscribe" className="tv-glass hover:bg-white/10 text-white font-semibold px-5 py-2.5 rounded-full text-sm">Upgrade to go live</Link>
        )}
      </div>

      {/* Sticky tab strip — so the user never loses navigation while
          deep-scrolling a section. On mobile the strip horizontally
          scrolls; visible scroll indicator lets the user discover the
          12 tabs instead of getting trapped on the first 3. */}
      <div className="sticky top-16 z-20 bg-black/85 backdrop-blur-md -mx-4 sm:-mx-6 px-4 sm:px-6 border-b border-white/10 mb-6">
        <div className="flex gap-1 sm:gap-2 pt-2 pb-2 overflow-x-auto sm:overflow-x-visible hide-scrollbar">
          <TabButton active={tab === "overview"} onClick={() => setTab("overview")} icon={BarChart2} testId="tab-overview">Overview</TabButton>
          <TabButton active={tab === "upload"} onClick={() => setTab("upload")} icon={Upload} testId="tab-upload">Upload</TabButton>
          <TabButton active={tab === "import"} onClick={() => setTab("import")} icon={LinkIcon} testId="tab-import">Import</TabButton>
          <TabButton active={tab === "golive"} onClick={() => setTab("golive")} icon={Radio} testId="tab-golive">Go Live</TabButton>
          <TabButton active={tab === "blast"} onClick={() => setTab("blast")} icon={Mail} testId="tab-blast">Email</TabButton>
          <TabButton active={tab === "sms"} onClick={() => setTab("sms")} icon={MessageCircle} testId="tab-sms">SMS</TabButton>
          <TabButton active={tab === "fanclub"} onClick={() => setTab("fanclub")} icon={Crown} testId="tab-fanclub">Fan Club</TabButton>
          <TabButton active={tab === "library"} onClick={() => setTab("library")} icon={Library} testId="tab-library">Library</TabButton>
          <TabButton active={tab === "goals"} onClick={() => setTab("goals")} icon={BarChart2} testId="tab-goals">Goals</TabButton>
          <TabButton active={tab === "tips"} onClick={() => setTab("tips")} icon={DollarSign} testId="tab-tips">Tips</TabButton>
          <TabButton active={tab === "earnings"} onClick={() => setTab("earnings")} icon={DollarSign} testId="tab-earnings">Earnings</TabButton>
          <TabButton active={tab === "shop"} onClick={() => setTab("shop")} icon={Upload} testId="tab-shop">Shop</TabButton>
        </div>
      </div>

      {tab === "overview" && analytics && (
        <div>
          {/* Story replies inbox — quick-glance card at top of Overview */}
          <StoryInboxCard />
          <DraftsCard />
          <NotifyHooksCard />
          <SocialLinksCard />
          <DeleteAccountCard />

          {/* Promo: Start your own Radio Show */}
          <Link
            to="/radio/host"
            data-testid="studio-radio-host-promo"
            className="group block mb-6 rounded-2xl overflow-hidden bg-gradient-to-r from-fuchsia-600/30 via-purple-600/25 to-tv-accent/25 border border-fuchsia-500/40 p-5 hover:from-fuchsia-600/45 hover:to-tv-accent/40 transition"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-fuchsia-500/25 border border-fuchsia-500/50 flex items-center justify-center shrink-0">
                <Radio className="w-7 h-7 text-fuchsia-200" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-fuchsia-200 text-[10px] uppercase tracking-[0.25em] font-black mb-0.5">New · Tap IN Radio</div>
                <div className="text-white font-display font-black text-xl leading-tight">Start your own Radio Show</div>
                <div className="text-white/70 text-sm mt-1">
                  Dual-deck DJ studio · crossfader · voice FX · ad scheduler · MIDI controller support. Apply in 30 seconds.
                </div>
              </div>
              <div className="text-fuchsia-200 bg-black/30 border border-fuchsia-500/40 group-hover:bg-fuchsia-500 group-hover:text-black font-bold text-sm px-4 py-2 rounded-full shrink-0">
                Launch Studio →
              </div>
            </div>
          </Link>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Stat label="Videos" value={analytics.total_videos} />
            <Stat label="Total Views" value={analytics.total_views.toLocaleString()} />
            <Stat label="Likes" value={analytics.total_likes.toLocaleString()} />
            <Stat label="Followers" value={analytics.followers.toLocaleString()} />
            <Stat label="Est. Revenue" value={`$${analytics.est_revenue_usd}`} accent />
          </div>
          <FormatBreakdown />
          <section className="mt-10">
            <h2 className="font-display font-bold text-2xl text-white mb-4">Top Videos</h2>
            <div className="space-y-2">
              {analytics.top_videos.map((v) => (
                <div key={v.id} className="tv-glass rounded-xl p-3 flex items-center gap-4" data-testid={`top-video-${v.id}`}>
                  <img src={v.thumbnail_url} alt="" className="w-24 h-14 object-cover rounded" />
                  <div className="flex-1">
                    <div className="text-white font-semibold">{v.title}</div>
                    <div className="text-xs text-white/50">{v.category}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-white font-bold">{v.view_count?.toLocaleString()}</div>
                    <div className="text-xs text-white/50">views</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === "upload" && <UploadForm cats={cats} onUploaded={() => api.get(`/videos?creator_id=${user.user_id}&limit=100`).then((r) => setMyVideos(r.data))} />}
      {tab === "import" && (
        <SocialImport
          categories={cats}
          onImported={() => api.get(`/videos?creator_id=${user.user_id}&limit=100`).then((r) => setMyVideos(r.data))}
        />
      )}
      {tab === "golive" && <GoLiveTab user={user} />}
      {tab === "blast" && <EmailBlastPanel user={user} />}
      {tab === "sms" && <SmsBlastPanel user={user} />}
      {tab === "fanclub" && <FanClubPanel />}
      {tab === "library" && <Library_ videos={myVideos} onChange={() => api.get(`/videos?creator_id=${user.user_id}&limit=100`).then((r) => setMyVideos(r.data))} />}
      {tab === "goals" && <GoalsTab />}
      {tab === "tips" && <TipsTab />}
      {tab === "shop" && <StudioShopTab />}
      {tab === "earnings" && <Earnings analytics={analytics} />}

      {/* Mobile Quick-Action FAB — one-tap shortcuts to Go Live / Record /
          Draft News, sits above the floating MobileBottomNav */}
      <StudioMobileFab />
    </div>
  );
}

function GoalsTab() {
  const [goals, setGoals] = useState(null);
  useEffect(() => { api.get("/creator/goals").then((r) => setGoals(r.data)); }, []);
  if (!goals) return null;
  return (
    <div>
      <div className="text-white/60 text-sm mb-5">Milestones that unlock badges, confetti, and new features.</div>
      <div className="grid md:grid-cols-2 gap-4">
        {goals.milestones.map((m) => {
          const pct = Math.min(100, (m.current / m.target) * 100);
          const done = m.current >= m.target;
          return (
            <div key={m.key} data-testid={`goal-${m.key}`} className={`tv-glass rounded-xl p-5 ${done ? "border-tv-accent/50" : ""}`}>
              <div className="flex items-center justify-between">
                <div className="font-display font-bold text-white">{m.label}</div>
                {done && <div className="text-xs bg-tv-accent text-black font-bold px-2 py-0.5 rounded-full">Unlocked 🏆</div>}
              </div>
              <div className="mt-3 tv-progress"><div style={{ width: `${pct}%` }} /></div>
              <div className="mt-2 text-xs text-white/60">{m.current.toLocaleString()} / {m.target.toLocaleString()}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TipsTab() {
  const [tips, setTips] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [referral, setReferral] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);
  const [payoutAmt, setPayoutAmt] = useState("");
  const [busy, setBusy] = useState(false);

  const loadEarnings = async () => {
    try { const r = await api.get("/creator/earnings"); setEarnings(r.data); } catch {}
  };
  const loadReferral = async () => {
    try { const r = await api.get("/creator/referral"); setReferral(r.data); } catch {}
  };
  useEffect(() => {
    api.get("/creator/tips/recent").then((r) => setTips(r.data)).catch(() => {});
    loadEarnings();
    loadReferral();
  }, []);

  const requestPayout = async () => {
    const amt = parseFloat(payoutAmt);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (earnings && amt > earnings.available_balance) { toast.error(`Max available: $${earnings.available_balance.toFixed(2)}`); return; }
    setBusy(true);
    try {
      await api.post("/creator/payout-request", { amount: amt, method: "bank" });
      toast.success("Payout requested");
      setPayoutAmt("");
      await loadEarnings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payout failed");
    } finally { setBusy(false); }
  };

  const sendDigest = async () => {
    setDigestBusy(true);
    try {
      const { data } = await api.post("/creator/digest/preview");
      if (data.email_sent) toast.success("Weekly digest emailed to you");
      else toast.info(data.detail?.error?.includes("verify a domain") ? "Test mode: email only sends to the Resend account owner until domain is verified." : "Could not send digest");
    } finally { setDigestBusy(false); }
  };

  const copyReferral = () => {
    if (!referral) return;
    const url = `${window.location.origin}${referral.share_url}`;
    navigator.clipboard?.writeText(url);
    toast.success("Referral link copied");
  };

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Available balance" value={`$${(earnings?.available_balance || 0).toFixed(2)}`} accent />
        <Stat label="Lifetime tips" value={`$${(earnings?.lifetime_tips || 0).toFixed(2)}`} />
        <Stat label="Pending payouts" value={`$${(earnings?.pending_payouts || 0).toFixed(2)}`} />
        <Stat label="Paid out" value={`$${(earnings?.paid_payouts || 0).toFixed(2)}`} />
      </div>

      <div data-testid="payout-request-card" className="tv-glass rounded-2xl p-5 mb-6 flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wider text-white/50 font-semibold mb-1">Request payout</div>
          <div className="text-xs text-white/50 mb-2">Platform keeps {earnings?.platform_cut_pct ?? 15}% of each tip. Withdraw any time.</div>
          <input
            data-testid="payout-amount-input"
            type="number"
            step="0.01"
            min="0"
            value={payoutAmt}
            onChange={(e) => setPayoutAmt(e.target.value)}
            placeholder="Amount in USD"
            className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-tv-brand"
          />
        </div>
        <button
          data-testid="payout-request-btn"
          disabled={busy}
          onClick={requestPayout}
          className="bg-tv-accent hover:bg-yellow-400 text-black font-bold px-6 py-2 rounded-full disabled:opacity-60"
        >
          {busy ? "Requesting…" : "Request Payout"}
        </button>
      </div>

      {referral && (
        <div data-testid="referral-card" className="tv-glass rounded-2xl p-5 mb-6 border border-tv-brand/20">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-widest text-tv-brand font-bold">Referral program · 10%</div>
              <div className="font-display font-bold text-white text-xl mt-1">Invite creators. Earn 10% of their first-year revenue.</div>
              <div className="text-xs text-white/50 mt-1">{referral.total_referrals} active referrals · est. bonus ${referral.est_bonus_usd.toFixed(2)}</div>
            </div>
            <div className="flex items-center gap-2">
              <code data-testid="referral-code" className="bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-tv-accent text-sm font-mono">{referral.code}</code>
              <button data-testid="referral-copy-btn" onClick={copyReferral} className="bg-tv-brand hover:bg-yellow-500 text-black font-bold px-4 py-1.5 rounded-full text-sm">Copy link</button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6">
        <button data-testid="digest-preview-btn" onClick={sendDigest} disabled={digestBusy} className="tv-glass border border-white/10 hover:bg-white/10 text-white px-4 py-2 rounded-full text-sm">
          {digestBusy ? "Sending…" : "Email me a preview of my weekly digest"}
        </button>
      </div>

      {tips.length === 0 ? <div className="text-white/50">No tips yet — viewers can send you a tip on any video page.</div> : (
        <div className="space-y-2">
          {tips.map((t) => (
            <div key={t.id} className="tv-glass rounded-xl p-3 flex items-center justify-between">
              <div>
                <div className="text-white font-semibold">{t.from_user_name || "Anonymous"} <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${t.status === "paid" ? "bg-green-500/20 text-green-300" : "bg-white/10 text-white/60"}`}>{t.status}</span></div>
                <div className="text-xs text-white/60">{t.message || "— no message —"}</div>
              </div>
              <div className="font-bold text-tv-accent">${t.amount?.toFixed(2)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`tv-glass rounded-xl p-5 ${accent ? "border-tv-accent/40" : ""}`}>
      <div className="text-xs uppercase tracking-wider text-white/50 font-semibold">{label}</div>
      <div className={`font-display font-black text-3xl mt-1 ${accent ? "text-tv-accent" : "text-white"}`}>{value}</div>
    </div>
  );
}

function UploadForm({ cats, onUploaded }) {
  const [file, setFile] = useState(null);
  const [thumbFile, setThumbFile] = useState(null);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [category, setCategory] = useState("films");
  const [isPremium, setIsPremium] = useState(false);
  const [membersOnly, setMembersOnly] = useState(false);
  const [externalUrl, setExternalUrl] = useState("");
  const [externalThumb, setExternalThumb] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("upload"); // "upload" | "record"
  const [format, setFormat] = useState("wide"); // "story" | "wide"
  const [aspectRatio, setAspectRatio] = useState("16:9");
  const [autoDetected, setAutoDetected] = useState(false);
  const [waiverOpen, setWaiverOpen] = useState(false);
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [isPpv, setIsPpv] = useState(false);
  const [ppvPrice, setPpvPrice] = useState(4.99);
  const [ppvPreviewSec, setPpvPreviewSec] = useState(30);
  const [ppvEventAt, setPpvEventAt] = useState("");
  const [captionsFile, setCaptionsFile] = useState(null);
  const dropRef = useRef(null);

  useEffect(() => {
    api.get("/waiver/status").then((r) => setWaiverAccepted(!!r.data?.accepted)).catch(() => {});
  }, []);

  // Auto-detect aspect ratio when file is picked
  useEffect(() => {
    if (!file) { setAutoDetected(false); return; }
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => {
      const w = v.videoWidth, h = v.videoHeight;
      if (w && h) {
        const ratio = w / h;
        const detected = ratio < 1 ? "story" : "wide";
        setFormat(detected);
        setAspectRatio(detected === "story" ? "9:16" : "16:9");
        setAutoDetected(true);
        toast.info(`Detected ${detected === "story" ? "Story (9:16)" : "Wide (16:9)"} — you can override below`);
      }
      URL.revokeObjectURL(url);
    };
    v.src = url;
  }, [file]);

  // Sync aspect ratio when format toggled manually
  useEffect(() => {
    setAspectRatio(format === "story" ? "9:16" : "16:9");
  }, [format]);

  const doUpload = async () => {
    setErr(""); setBusy(true);
    try {
      let storage_path = null, thumbnail_url = externalThumb || null, video_url = externalUrl || null;

      if (file) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/uploads/video", fd, { headers: { "Content-Type": "multipart/form-data" } });
        storage_path = data.storage_path;
        video_url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.storage_path}`;
      }
      if (thumbFile) {
        const fd = new FormData(); fd.append("file", thumbFile);
        const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        thumbnail_url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.storage_path}`;
      }

      let captions_vtt_url = null;
      if (captionsFile) {
        const fd = new FormData(); fd.append("file", captionsFile);
        const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        captions_vtt_url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.storage_path}`;
      }

      if (!video_url) { setErr("Provide an uploaded video or an external URL"); setBusy(false); return; }

      await api.post("/videos", {
        title, description: desc, category, tags: [category], is_premium: isPremium,
        members_only: membersOnly,
        video_url, thumbnail_url, storage_path,
        is_short: format === "story",
        format,
        aspect_ratio: aspectRatio,
        is_ppv: isPpv,
        ppv_price_usd: isPpv ? Number(ppvPrice) : null,
        ppv_preview_seconds: isPpv ? Number(ppvPreviewSec) || 30 : null,
        ppv_event_at: isPpv && ppvEventAt ? new Date(ppvEventAt).toISOString() : null,
        captions_vtt_url,
      });
      toast.success(format === "story" ? "Story published to Stories feed" : "Video published to Movies");
      setTitle(""); setDesc(""); setFile(null); setThumbFile(null); setExternalUrl(""); setExternalThumb("");
      setAutoDetected(false);
      onUploaded();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };

  const upload = async () => {
    if (!title.trim()) return setErr("Title is required");
    if (!waiverAccepted) { setWaiverOpen(true); return; }
    await doUpload();
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) setFile(f);
  };

  const onRecorded = (recordedFile, recFormat) => {
    setFile(recordedFile);
    setFormat(recFormat);
    setAspectRatio(recFormat === "story" ? "9:16" : "16:9");
    setMode("upload");
    toast.success(`Recorded ${recFormat === "story" ? "Story" : "Wide"} clip attached`);
  };

  return (
    <div className="space-y-6">
      <WaiverModal open={waiverOpen} onClose={() => setWaiverOpen(false)} onAccepted={() => { setWaiverAccepted(true); setWaiverOpen(false); doUpload(); }} />
      {/* Mode + Format toggles */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex tv-glass rounded-full p-1" role="tablist">
          <button
            data-testid="upload-mode-upload"
            type="button"
            onClick={() => setMode("upload")}
            className={`px-4 py-1.5 text-xs font-bold rounded-full transition ${mode === "upload" ? "bg-white text-black" : "text-white/70"}`}
          >
            Upload File
          </button>
          <button
            data-testid="upload-mode-record"
            type="button"
            onClick={() => setMode("record")}
            className={`px-4 py-1.5 text-xs font-bold rounded-full transition ${mode === "record" ? "bg-white text-black" : "text-white/70"}`}
          >
            Record Now
          </button>
        </div>

        <div className="inline-flex tv-glass rounded-full p-1" role="tablist">
          <button
            data-testid="upload-format-story"
            type="button"
            onClick={() => setFormat("story")}
            className={`px-4 py-1.5 text-xs font-bold rounded-full transition ${format === "story" ? "bg-tv-accent text-black" : "text-white/70"}`}
          >
            Story · 9:16
          </button>
          <button
            data-testid="upload-format-wide"
            type="button"
            onClick={() => setFormat("wide")}
            className={`px-4 py-1.5 text-xs font-bold rounded-full transition ${format === "wide" ? "bg-tv-accent text-black" : "text-white/70"}`}
          >
            Wide · 16:9
          </button>
        </div>

        {autoDetected && (
          <span data-testid="upload-format-auto" className="text-[10px] uppercase tracking-widest text-tv-brand font-bold">
            Auto-detected
          </span>
        )}
      </div>

      {mode === "record" && (
        <VideoRecorder format={format} onFormatChange={setFormat} onRecorded={onRecorded} />
      )}

      {mode === "upload" && (
        <div className="grid md:grid-cols-2 gap-8">
          <div
            ref={dropRef}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            data-testid="upload-dropzone"
            className="border-2 border-dashed border-white/20 hover:border-tv-brand bg-white/5 rounded-2xl p-10 flex flex-col items-center justify-center text-center cursor-pointer min-h-[360px] transition"
            onClick={() => document.getElementById("upload-file-input")?.click()}
          >
            <Upload className="w-12 h-12 text-white/40" />
            <div className="font-display font-bold text-2xl text-white mt-4">Drop video here</div>
            <div className="text-white/50 text-sm mt-1">or click to browse · MP4 / WebM</div>
            {file && <div className="mt-4 text-tv-brand text-sm font-semibold">{file.name} ({Math.round(file.size / 1e6)} MB)</div>}
            <input id="upload-file-input" type="file" accept="video/*" hidden onChange={(e) => setFile(e.target.files?.[0])} data-testid="upload-file-input" />
            <div className="mt-6 w-full">
              <div className="text-xs uppercase tracking-wider text-white/40 mb-2">Thumbnail (optional)</div>
              <input type="file" accept="image/*" onChange={(e) => setThumbFile(e.target.files?.[0])} className="text-xs text-white/70" data-testid="upload-thumb-input" />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Title</label>
              <input data-testid="upload-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white outline-none focus:border-tv-brand" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Description</label>
              <textarea data-testid="upload-desc" rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white outline-none focus:border-tv-brand" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Category</label>
                <select data-testid="upload-category" value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1 w-full bg-[#0B1120] border border-white/10 rounded-lg px-3 py-2.5 text-white outline-none focus:border-tv-brand">
                  {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 mt-6">
                <input type="checkbox" data-testid="upload-premium" checked={isPremium} onChange={(e) => setIsPremium(e.target.checked)} />
                <span className="text-white text-sm">Premium content</span>
              </label>
              <label className="flex items-center gap-2 mt-6">
                <input type="checkbox" data-testid="upload-members-only" checked={membersOnly} onChange={(e) => setMembersOnly(e.target.checked)} />
                <span className="text-white text-sm">Members-only (Fan Club)</span>
              </label>
            </div>
            <div className="border-t border-white/10 pt-4 grid grid-cols-3 gap-3 items-end">
              <label className="flex items-center gap-2 col-span-1">
                <input type="checkbox" data-testid="upload-ppv" checked={isPpv} onChange={(e) => setIsPpv(e.target.checked)} />
                <span className="text-white text-sm">Pay-per-view</span>
              </label>
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">PPV price (USD)</label>
                <input data-testid="upload-ppv-price" type="number" step="0.01" disabled={!isPpv} value={ppvPrice} onChange={(e) => setPpvPrice(e.target.value)} className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-tv-brand disabled:opacity-50" />
              </div>
            </div>
            {isPpv && (
              <div className="grid grid-cols-2 gap-3 items-end -mt-2">
                <div>
                  <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Free preview (seconds)</label>
                  <input
                    data-testid="upload-ppv-preview-sec"
                    type="number"
                    min="0"
                    max="600"
                    step="5"
                    value={ppvPreviewSec}
                    onChange={(e) => setPpvPreviewSec(e.target.value)}
                    className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-tv-brand"
                  />
                  <div className="text-[10px] text-white/40 mt-1">Viewers can sample this many seconds before the paywall.</div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Event date / time (optional)</label>
                  <input
                    data-testid="upload-ppv-event-at"
                    type="datetime-local"
                    value={ppvEventAt}
                    onChange={(e) => setPpvEventAt(e.target.value)}
                    className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-tv-brand"
                  />
                  <div className="text-[10px] text-white/40 mt-1">For ticketed live PPV events — drives the TV PPV channel countdown.</div>
                </div>
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Captions (.vtt)</label>
              <input data-testid="upload-captions-file" type="file" accept=".vtt,text/vtt" onChange={(e) => setCaptionsFile(e.target.files?.[0])} className="mt-1 text-xs text-white/70" />
            </div>
            <div className="border-t border-white/10 pt-4">
              <div className="text-xs uppercase tracking-wider text-white/40 mb-2">Or use external URL (skips upload)</div>
              <input data-testid="upload-ext-url" placeholder="https://…/video.mp4" value={externalUrl} onChange={(e) => setExternalUrl(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white outline-none focus:border-tv-brand text-sm" />
              <input data-testid="upload-ext-thumb" placeholder="https://…/thumbnail.jpg" value={externalThumb} onChange={(e) => setExternalThumb(e.target.value)} className="mt-2 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white outline-none focus:border-tv-brand text-sm" />
            </div>
            {err && <div className="text-sm text-red-400">{err}</div>}
            <button data-testid="upload-publish-btn" disabled={busy} onClick={upload} className="w-full bg-tv-accent text-black font-bold py-3 rounded-full disabled:opacity-60">
              {busy ? "Publishing…" : `Publish ${format === "story" ? "Story" : "Video"}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Library_({ videos, onChange }) {
  const [filter, setFilter] = useState("all"); // all | uploaded | imported
  const [busy, setBusy] = useState({});

  // Auto-refresh transcode status for any processing videos
  useEffect(() => {
    const processing = videos.filter((v) => v.transcode_status === "processing");
    if (processing.length === 0) return;
    let cancelled = false;
    const tick = async () => {
      let changed = false;
      for (const v of processing) {
        try {
          const { data } = await api.post(`/videos/${v.id}/transcode/refresh`);
          if (data.transcode_status === "ready") changed = true;
        } catch {}
      }
      if (!cancelled && changed) onChange();
    };
    const h = setInterval(tick, 8000);
    return () => { cancelled = true; clearInterval(h); };
  }, [videos, onChange]);

  const remove = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    await api.delete(`/videos/${id}`);
    toast.success("Deleted");
    onChange();
  };

  const rehost = async (v) => {
    setBusy((b) => ({ ...b, [v.id]: true }));
    try {
      await api.post(`/videos/${v.id}/transcode/cloudflare`);
      toast.success("Re-host started — Cloudflare Stream will transcode in the background.");
      onChange();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Re-host failed";
      toast.error(detail);
    } finally {
      setBusy((b) => ({ ...b, [v.id]: false }));
    }
  };

  const filtered = videos.filter((v) => {
    if (filter === "uploaded") return !v.is_external;
    if (filter === "imported") return v.is_external;
    return true;
  });

  if (!videos.length) return <div className="text-white/60">No videos yet — go to Upload to publish your first.</div>;

  const counts = {
    all: videos.length,
    uploaded: videos.filter((v) => !v.is_external).length,
    imported: videos.filter((v) => v.is_external).length,
  };

  return (
    <div>
      <div className="flex gap-2 mb-5" data-testid="library-filter-tabs">
        {["all", "uploaded", "imported"].map((k) => (
          <button
            key={k}
            data-testid={`library-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-wider font-bold transition ${filter === k ? "bg-tv-accent text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`}
          >
            {k} ({counts[k]})
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-white/60">No videos in this view.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((v) => {
            const drmLocked = ["instagram", "tiktok", "twitter", "facebook"].includes(v.source_type);
            const status = v.transcode_status || (v.is_external ? "embed_only" : "native");
            const canRehost = v.is_external && !drmLocked && status === "embed_only";
            return (
              <div key={v.id} data-testid={`library-video-${v.id}`} className="tv-glass rounded-xl overflow-hidden">
                <div className="aspect-video bg-black relative">
                  <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" />
                  {v.is_external && v.source_type && (
                    <div data-testid={`library-source-badge-${v.id}`} className="absolute top-2 left-2 bg-black/80 border border-tv-accent/40 text-tv-accent text-[10px] uppercase font-bold px-2 py-0.5 rounded tracking-wider">
                      From {v.source_type}
                    </div>
                  )}
                  {v.is_external && (
                    <div className="absolute top-2 right-2 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded"
                         style={{ background: status === "ready" ? "rgba(34,197,94,0.2)" : status === "processing" ? "rgba(59,130,246,0.2)" : "rgba(148,163,184,0.2)", color: status === "ready" ? "#4ade80" : status === "processing" ? "#60a5fa" : "#cbd5e1" }}
                         data-testid={`library-transcode-status-${v.id}`}>
                      {status === "ready" ? "Native HLS" : status === "processing" ? "Transcoding…" : "Embed only"}
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="font-display font-bold text-white text-lg line-clamp-1">{v.title}</div>
                  <div className="text-xs text-white/50 mt-1">{(v.view_count || 0).toLocaleString()} views · {(v.like_count || 0).toLocaleString()} likes</div>

                  {canRehost && (
                    <button
                      data-testid={`library-rehost-${v.id}`}
                      onClick={() => rehost(v)}
                      disabled={busy[v.id]}
                      className="mt-3 w-full bg-tv-accent hover:bg-yellow-400 disabled:opacity-60 text-black font-bold text-xs py-2 rounded-full flex items-center justify-center gap-2"
                    >
                      {busy[v.id] ? "Starting…" : "Re-host for native playback"}
                    </button>
                  )}
                  {v.is_external && drmLocked && (
                    <div className="mt-3 text-[10px] text-yellow-400/80 bg-yellow-400/10 border border-yellow-400/20 rounded px-2 py-1">
                      {v.source_type} content is DRM-protected — stays embedded.
                    </div>
                  )}

                  <div className="flex gap-2 mt-3">
                    <a href={`/watch/${v.id}`} className="text-sm text-tv-brand font-semibold">Watch</a>
                    <div className="flex-1" />
                    <button onClick={() => remove(v.id)} data-testid={`library-delete-${v.id}`} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FormatBreakdown() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/creator/formats-summary").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  const total = (d.story?.views || 0) + (d.wide?.views || 0);
  const storyPct = total ? Math.round((d.story.views / total) * 100) : 0;
  return (
    <section data-testid="format-breakdown" className="mt-8 tv-glass rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-white/50 font-semibold">Format engagement</div>
          <div className="text-white font-display font-bold text-xl mt-1">Story vs Wide</div>
        </div>
        <div className="text-right text-xs text-white/50">Based on total views</div>
      </div>
      <div className="relative h-2 bg-white/5 rounded-full overflow-hidden">
        <div className="absolute inset-y-0 left-0 bg-tv-accent" style={{ width: `${storyPct}%` }} />
        <div className="absolute inset-y-0 right-0 bg-tv-brand" style={{ width: `${100 - storyPct}%` }} />
      </div>
      <div className="grid grid-cols-2 gap-4 mt-4">
        <div data-testid="format-story-stat">
          <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-tv-accent" /><span className="text-white/70 text-xs uppercase tracking-wider font-semibold">Story (9:16)</span></div>
          <div className="text-white font-display font-black text-2xl mt-1">{d.story.views.toLocaleString()} <span className="text-xs text-white/50 font-sans font-normal">views</span></div>
          <div className="text-xs text-white/50">{d.story.videos} videos · {d.story.likes.toLocaleString()} likes</div>
        </div>
        <div data-testid="format-wide-stat" className="text-right">
          <div className="flex items-center gap-2 justify-end"><span className="text-white/70 text-xs uppercase tracking-wider font-semibold">Wide (16:9)</span><span className="w-2 h-2 rounded-full bg-tv-brand" /></div>
          <div className="text-white font-display font-black text-2xl mt-1">{d.wide.views.toLocaleString()} <span className="text-xs text-white/50 font-sans font-normal">views</span></div>
          <div className="text-xs text-white/50">{d.wide.videos} videos · {d.wide.likes.toLocaleString()} likes</div>
        </div>
      </div>
    </section>
  );
}

function Earnings({ analytics }) {
  if (!analytics) return null;
  return (
    <div className="tv-glass rounded-2xl p-8">
      <div className="text-xs uppercase tracking-wider text-white/50">Estimated Earnings (30d)</div>
      <div className="font-display font-black text-6xl text-tv-accent mt-2">${analytics.est_revenue_usd}</div>
      <div className="text-white/60 mt-2 text-sm">Based on {analytics.total_views.toLocaleString()} views across {analytics.total_videos} videos.</div>
      <div className="mt-8 grid md:grid-cols-3 gap-4">
        <Stat label="Subscriptions Share" value={`$${(analytics.est_revenue_usd * 0.5).toFixed(2)}`} />
        <Stat label="Ads Share" value={`$${(analytics.est_revenue_usd * 0.35).toFixed(2)}`} />
        <Stat label="Tips & PPV" value={`$${(analytics.est_revenue_usd * 0.15).toFixed(2)}`} />
      </div>
    </div>
  );
}

// =========================================================
// SHOP — creator product management + dropship import
// =========================================================
function StudioShopTab() {
  const [products, setProducts] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [form, setForm] = useState({ title: "", price_usd: "", description: "", category: "general", stock: 100, images: "" });
  const [view, setView] = useState("mine"); // "mine" | "dropship"

  const load = async () => {
    const p = await api.get("/creator/products"); setProducts(p.data || []);
    if (view === "dropship") {
      const c = await api.get("/shop/dropship-catalog"); setCatalog(c.data || []);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [view]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title || !form.price_usd) { toast.error("Title + price required"); return; }
    await api.post("/creator/products", {
      ...form, price_usd: Number(form.price_usd), stock: Number(form.stock),
      images: form.images.split(",").map((s) => s.trim()).filter(Boolean),
    });
    toast.success("Product listed");
    setForm({ title: "", price_usd: "", description: "", category: "general", stock: 100, images: "" });
    load();
  };

  const importDrop = async (item) => {
    await api.post(`/creator/dropship/import/${item.id}`, {});
    toast.success(`${item.title} added to your store`);
    setView("mine");
    load();
  };

  const remove = async (pid) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/creator/products/${pid}`);
    toast.info("Removed");
    load();
  };

  return (
    <div data-testid="studio-shop-tab">
      <div className="flex gap-2 mb-6">
        <button data-testid="shop-view-mine" onClick={() => setView("mine")} className={`px-4 py-1.5 rounded-full text-xs font-bold ${view === "mine" ? "bg-tv-accent text-black" : "tv-glass text-white border border-white/10"}`}>My Products ({products.length})</button>
        <button data-testid="shop-view-dropship" onClick={() => setView("dropship")} className={`px-4 py-1.5 rounded-full text-xs font-bold ${view === "dropship" ? "bg-tv-accent text-black" : "tv-glass text-white border border-white/10"}`}>Dropship Catalog</button>
      </div>

      {view === "mine" && (
        <>
          <form onSubmit={create} data-testid="product-create-form" className="tv-glass rounded-2xl p-5 grid md:grid-cols-2 gap-3 mb-6">
            <input data-testid="product-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" required />
            <input data-testid="product-price" type="number" step="0.01" placeholder="Price USD" value={form.price_usd} onChange={(e) => setForm({ ...form, price_usd: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" required />
            <textarea data-testid="product-desc" placeholder="Description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="md:col-span-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
            <input data-testid="product-category" placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
            <input data-testid="product-stock" type="number" placeholder="Stock" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
            <input data-testid="product-images" placeholder="Image URLs (comma-separated)" value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} className="md:col-span-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
            <button data-testid="product-create-btn" type="submit" className="md:col-span-2 bg-tv-accent text-black font-bold py-2 rounded-full">List Product</button>
          </form>

          {products.length === 0 ? <div className="text-white/60">No products yet.</div> : (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {products.map((p) => (
                <div key={p.id} data-testid={`my-product-${p.id}`} className="tv-glass rounded-xl overflow-hidden">
              <img src={(p.images || [])[0] || "https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=400&q=80"} alt="" className="w-full aspect-square object-cover" />
                  <div className="p-3">
                    <div className="text-white font-semibold text-sm truncate">{p.title}</div>
                    <div className="text-xs text-white/50">{p.sales_count} sold · {p.stock} in stock</div>
                    <div className="flex justify-between items-center mt-2">
                      <div className="text-tv-accent font-bold">${p.price_usd.toFixed(2)}</div>
                      {p.is_dropship && <span className="text-[9px] uppercase bg-purple-500/20 text-purple-300 font-bold px-2 py-0.5 rounded">Dropship</span>}
                    </div>
                    <button data-testid={`my-product-delete-${p.id}`} onClick={() => remove(p.id)} className="mt-2 w-full text-red-400 text-xs hover:text-red-300">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {view === "dropship" && (
        <div data-testid="dropship-list">
          <p className="text-white/60 text-sm mb-4">Pick products from the network catalog. We handle supplier fulfillment; you keep the margin above the supplier price.</p>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {catalog.map((item) => (
              <div key={item.id} data-testid={`dropship-item-${item.id}`} className="tv-glass rounded-xl overflow-hidden">
                <img src={(item.images || [])[0] || "https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=400&q=80"} alt="" className="w-full aspect-square object-cover" />
                <div className="p-3">
                  <div className="text-white font-semibold text-sm truncate">{item.title}</div>
                  <div className="text-xs text-white/50 truncate">by {item.vendor}</div>
                  <div className="flex justify-between items-center mt-2 text-xs">
                    <span className="text-white/50">Cost <span className="text-white font-bold">${item.supplier_price_usd.toFixed(2)}</span></span>
                    <span className="text-tv-accent font-bold">Sell ${item.suggested_price_usd.toFixed(2)}</span>
                  </div>
                  <button data-testid={`dropship-import-${item.id}`} onClick={() => importDrop(item)} className="mt-3 w-full bg-tv-accent text-black font-bold text-xs py-2 rounded-full">Add to my store</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}



function GoLiveTab({ user }) {
  const [creating, setCreating] = useState(false);
  const [activeStream, setActiveStream] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/live?creator_id=${user.user_id}`).then((r) => {
      const mine = (r.data || []).find((s) => s.creator_id === user.user_id && s.is_live);
      if (mine) setActiveStream(mine);
    }).catch(() => {});
  }, [user.user_id]);

  const startStream = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/live", { title: `${user.name} — Live`, description: "Going live", category: "behind_the_scenes" });
      setActiveStream(data);
      toast.success("Stream ready. Paste the RTMPS creds into OBS and hit Start Streaming.");
    } catch (e) { toast.error(formatApiError(e)); } finally { setCreating(false); }
  };

  const stopStream = async () => {
    if (!activeStream) return;
    try { await api.post(`/live/${activeStream.id}/stop`); setActiveStream(null); toast.success("Stream ended"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (user.role !== "creator" && user.role !== "premium_creator" && user.role !== "admin") {
    return (
      <div className="tv-glass rounded-2xl p-10 text-center max-w-xl mx-auto" data-testid="golive-upgrade-prompt">
        <h3 className="font-display font-black text-2xl text-white">Become a creator to go live</h3>
        <p className="text-white/60 mt-2">Apply for a creator account, then stream anytime. Premium unlocks Director's Mode.</p>
        <Link to="/apply-creator" className="inline-block mt-4 bg-tv-accent text-black font-bold px-6 py-2.5 rounded-full">Apply now</Link>
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-[1fr_auto] gap-6 items-start">
      <GoLiveOBSPanel streamId={activeStream?.id} />
      <div className="tv-glass rounded-2xl border border-white/10 p-6 w-full md:w-80">
        <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-bold">Quick actions</div>
        {activeStream ? (
          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-2 text-sm text-white">
              <span className="w-2 h-2 rounded-full bg-tv-live animate-pulse" /> <span className="font-bold">LIVE</span> — {activeStream.title}
            </div>
            <button data-testid="golive-view-public-btn" onClick={() => nav(`/live/${activeStream.id}`)} className="w-full bg-white/10 hover:bg-white/20 text-white font-semibold py-2.5 rounded-full text-sm">View public page →</button>
            <button data-testid="golive-directors-mode-btn" onClick={() => nav("/studio/live")} className="w-full bg-white/10 hover:bg-white/20 text-white font-semibold py-2.5 rounded-full text-sm">Open Director's Mode</button>
            <button data-testid="golive-stop-btn" onClick={stopStream} className="w-full bg-tv-live hover:bg-red-600 text-white font-bold py-2.5 rounded-full text-sm">End stream</button>
          </div>
        ) : (
          <button data-testid="golive-start-btn" onClick={startStream} disabled={creating} className="mt-4 w-full bg-tv-live hover:bg-red-600 disabled:opacity-50 text-white font-bold py-3 rounded-full text-sm flex items-center justify-center gap-2">
            <Radio className="w-4 h-4" /> {creating ? "Starting…" : "Start a new live stream"}
          </button>
        )}
        <div className="text-[11px] text-white/50 mt-4">Starting a stream provisions an RTMPS ingest and shows the Stream Key + URL to paste into OBS.</div>
      </div>
    </div>
  );
}

function StoryInboxCard() {
  const [data, setData] = useState({ messages: [], unread: 0 });
  const [expanded, setExpanded] = useState(false);

  const load = () => api.get("/story-inbox?limit=20").then((r) => setData(r.data || { messages: [], unread: 0 })).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60_000); return () => clearInterval(t); }, []);

  const markRead = async (mid) => {
    try {
      await api.post(`/story-inbox/${mid}/read`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (data.messages.length === 0 && !data.unread) {
    return null;
  }

  const shown = expanded ? data.messages : data.messages.slice(0, 3);

  return (
    <section data-testid="studio-story-inbox" className="mb-6 tv-glass rounded-2xl border border-white/10 p-5">
      <div className="flex items-center gap-2 mb-3">
        <Inbox className="w-5 h-5 text-tv-accent" />
        <h2 className="font-display font-bold text-xl text-white">Story replies</h2>
        {data.unread > 0 && (
          <span data-testid="story-inbox-unread" className="ml-1 bg-tv-live text-white text-[10px] font-black px-2 py-0.5 rounded-full">
            {data.unread} new
          </span>
        )}
        {data.messages.length > 3 && (
          <button
            onClick={() => setExpanded((e) => !e)}
            className="ml-auto text-white/60 hover:text-white text-xs font-bold"
          >{expanded ? "Show less" : `Show all (${data.messages.length})`}</button>
        )}
      </div>
      <div className="space-y-2">
        {shown.map((m) => (
          <div
            key={m.id}
            data-testid={`story-inbox-row-${m.id}`}
            className={`flex items-start gap-3 rounded-lg p-3 ${m.read_at ? "bg-white/5" : "bg-tv-accent/10 border border-tv-accent/30"}`}
          >
            {m.from_user_avatar ? (
              <img src={m.from_user_avatar} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white/60 font-bold shrink-0">
                {(m.from_user_name || "?")[0]}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-xs">
                <Link to={`/creator/${m.from_user_id}`} className="text-white font-bold hover:underline truncate">{m.from_user_name}</Link>
                <span className="text-white/40">· on "{m.story_title}"</span>
                <span className="text-white/30 ml-auto whitespace-nowrap">{new Date(m.created_at).toLocaleString()}</span>
              </div>
              <div className="text-white/90 text-sm mt-1 break-words">{m.text}</div>
            </div>
            {m.story_thumb && (
              <img src={m.story_thumb} alt="" className="w-12 h-16 object-cover rounded shrink-0 hidden sm:block" />
            )}
            {!m.read_at && (
              <button
                data-testid={`story-inbox-read-${m.id}`}
                onClick={() => markRead(m.id)}
                className="text-[10px] uppercase tracking-wider font-bold bg-white/10 hover:bg-white/15 text-white px-2 py-1 rounded shrink-0"
              >Mark read</button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}


function NotifyHooksCard() {
  const [hooks, setHooks] = useState([]);
  const [events, setEvents] = useState(["story_reply", "story_reaction", "live_start", "highlight_added"]);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [newEvents, setNewEvents] = useState(["story_reply", "story_reaction", "live_start"]);
  const [busyId, setBusyId] = useState(null);

  const load = () => api.get("/creator/notify-hooks").then((r) => {
    setHooks(r.data?.hooks || []);
    if (r.data?.available_events) setEvents(r.data.available_events);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const addHook = async () => {
    if (!newUrl.trim()) { toast.error("URL required"); return; }
    setBusyId("_new");
    try {
      await api.post("/creator/notify-hooks", { label: newLabel, url: newUrl.trim(), events: newEvents });
      toast.success("Webhook added");
      setNewLabel(""); setNewUrl(""); setAdding(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const toggleEvent = (ev, current, setter) => {
    setter(current.includes(ev) ? current.filter((x) => x !== ev) : [...current, ev]);
  };

  const test = async (h) => {
    setBusyId(h.id);
    try {
      const { data } = await api.post(`/creator/notify-hooks/${h.id}/test`);
      if (data?.result?.ok) toast.success(`Pinged (${data.result.status})`);
      else toast.error(`Webhook failed ${data?.result?.status || "err"}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const toggle = async (h) => {
    try {
      await api.patch(`/creator/notify-hooks/${h.id}`, { enabled: !h.enabled });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (h) => {
    if (!window.confirm(`Delete "${h.label}"?`)) return;
    try {
      await api.delete(`/creator/notify-hooks/${h.id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section data-testid="studio-notify-hooks" className="mb-6 tv-glass rounded-2xl border border-white/10 p-5">
      <div className="flex items-center gap-2 mb-1">
        <Bell className="w-5 h-5 text-tv-accent" />
        <h2 className="font-display font-bold text-xl text-white">Push notifications</h2>
        <span className="ml-auto text-white/40 text-xs">Zapier · Slack · Discord · Make</span>
      </div>
      <p className="text-white/60 text-sm mb-4">Get pinged when someone replies to your story, hearts it, or when you go live. Paste a webhook URL from any provider and pick which events fire.</p>

      {hooks.length === 0 && !adding && (
        <div className="text-white/50 text-sm" data-testid="notify-hooks-empty">No webhooks yet. Add one to route Tap IN events to email, SMS, Slack, or anywhere.</div>
      )}

      <div className="space-y-2">
        {hooks.map((h) => (
          <div key={h.id} data-testid={`notify-hook-${h.id}`} className="bg-black/40 border border-white/10 rounded-lg p-3 flex items-center gap-3 flex-wrap">
            <div className={`w-2 h-2 rounded-full ${h.enabled ? "bg-green-400" : "bg-white/30"}`} />
            <div className="flex-1 min-w-0">
              <div className="text-white font-bold text-sm truncate">{h.label}</div>
              <div className="text-white/50 text-xs truncate">{h.url}</div>
              <div className="flex flex-wrap gap-1 mt-1">
                {(h.events || []).map((e) => (
                  <span key={e} className="text-[10px] bg-white/10 text-white/70 px-1.5 py-0.5 rounded uppercase tracking-wider font-bold">{e.replace(/_/g, " ")}</span>
                ))}
              </div>
            </div>
            <button
              data-testid={`notify-hook-test-${h.id}`}
              onClick={() => test(h)}
              disabled={busyId === h.id}
              className="inline-flex items-center gap-1 text-xs bg-white/10 hover:bg-white/15 text-white font-bold px-3 py-1.5 rounded-full"
            ><SendIcon className="w-3 h-3" /> Test</button>
            <button
              data-testid={`notify-hook-toggle-${h.id}`}
              onClick={() => toggle(h)}
              className={`text-xs font-bold px-3 py-1.5 rounded-full ${h.enabled ? "bg-tv-accent text-black" : "bg-white/10 text-white/70"}`}
            >{h.enabled ? "On" : "Off"}</button>
            <button
              data-testid={`notify-hook-delete-${h.id}`}
              onClick={() => del(h)}
              className="p-1.5 text-white/50 hover:text-red-400"
            ><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      {adding ? (
        <div className="mt-3 bg-black/30 border border-white/10 rounded-lg p-3 space-y-2">
          <input
            data-testid="notify-hook-new-label"
            value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
            maxLength={60} placeholder="Label · e.g. 'My Slack'"
            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent"
          />
          <input
            data-testid="notify-hook-new-url"
            value={newUrl} onChange={(e) => setNewUrl(e.target.value)}
            placeholder="https://hooks.slack.com/…"
            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent"
          />
          <div className="flex flex-wrap gap-2">
            {events.map((ev) => (
              <button
                key={ev}
                data-testid={`notify-hook-event-${ev}`}
                onClick={() => toggleEvent(ev, newEvents, setNewEvents)}
                className={`text-[11px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${newEvents.includes(ev) ? "bg-tv-accent text-black" : "bg-white/10 text-white/60"}`}
              >{ev.replace(/_/g, " ")}</button>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setAdding(false)} className="flex-1 bg-white/10 hover:bg-white/15 text-white font-bold py-2 rounded-full text-sm">Cancel</button>
            <button
              data-testid="notify-hook-save-new"
              onClick={addHook}
              disabled={busyId === "_new" || !newUrl.trim()}
              className="flex-1 bg-tv-accent disabled:opacity-40 hover:bg-tv-accent/90 text-black font-bold py-2 rounded-full text-sm"
            >{busyId === "_new" ? "Saving…" : "Add webhook"}</button>
          </div>
        </div>
      ) : (
        <button
          data-testid="notify-hook-add-btn"
          onClick={() => setAdding(true)}
          className="mt-3 inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/15 text-white font-bold px-4 py-2 rounded-full text-sm"
        ><PlusIcon className="w-4 h-4" /> Add webhook</button>
      )}
    </section>
  );
}

/**
 * SocialLinksCard — creators + viewers publish their off-platform
 * social handles (YouTube channel, Instagram, TikTok, Twitter, Facebook,
 * Website) so viewers can jump between their Tap In presence and
 * everywhere else they post. Empty fields are hidden on their profile;
 * bare handles get https:// auto-prepended by the backend.
 */
function SocialLinksCard() {
  const { user, setUser } = useAuth();
  const initial = (user && user.social_links) || {};
  const [links, setLinks] = useState({
    youtube: initial.youtube || "",
    instagram: initial.instagram || "",
    tiktok: initial.tiktok || "",
    twitter: initial.twitter || "",
    facebook: initial.facebook || "",
    website: initial.website || "",
  });
  // Auto-tweet toggle — when ON + user has a Twitter handle saved, the
  // LiveBroadcaster opens a pre-filled tweet compose window the moment
  // status transitions to "live". Persisted via localStorage (no
  // server auth needed — uses the public intent/tweet endpoint).
  const [autoTweet, setAutoTweet] = useState(() => {
    try { return localStorage.getItem("tv_auto_tweet_on_live") === "1"; }
    catch { return false; }
  });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const set = (k, v) => { setLinks((p) => ({ ...p, [k]: v })); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", { social_links: links });
      setUser?.(data);
      try { localStorage.setItem("tv_auto_tweet_on_live", autoTweet ? "1" : "0"); } catch { /* quota */ }
      setDirty(false);
      toast.success("Social links saved.");
    } catch (e) { toast.error(formatApiError(e) || "Couldn't save links."); }
    finally { setSaving(false); }
  };
  const fields = [
    { k: "youtube",   label: "YouTube channel",  ph: "youtube.com/@yourhandle" },
    { k: "instagram", label: "Instagram",        ph: "instagram.com/yourhandle" },
    { k: "tiktok",    label: "TikTok",           ph: "tiktok.com/@yourhandle" },
    { k: "twitter",   label: "X / Twitter",      ph: "twitter.com/yourhandle" },
    { k: "facebook",  label: "Facebook",         ph: "facebook.com/yourpage" },
    { k: "website",   label: "Website",          ph: "yoursite.com" },
  ];
  return (
    <section data-testid="social-links-card" className="tv-glass rounded-2xl p-4 sm:p-5 mb-6">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-display font-black text-white text-lg">Your links</div>
          <div className="text-white/50 text-xs mt-0.5">Show viewers your YouTube + socials. Fills your public profile automatically.</div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {fields.map(({ k, label, ph }) => (
          <label key={k} className="block">
            <div className="text-[10px] uppercase tracking-wider text-white/50 font-black mb-1">{label}</div>
            <input
              type="text"
              value={links[k]}
              onChange={(e) => set(k, e.target.value)}
              placeholder={ph}
              maxLength={400}
              data-testid={`social-link-${k}`}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent placeholder:text-white/25"
            />
          </label>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        {/* Auto-tweet toggle — opens a pre-filled X/Twitter compose the
            moment the creator goes live. Requires the Twitter link
            above to be filled (we don't share otherwise). */}
        <label className="inline-flex items-center gap-2 text-xs text-white/80 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={autoTweet}
            onChange={(e) => { setAutoTweet(e.target.checked); setDirty(true); }}
            data-testid="social-links-auto-tweet"
            className="accent-tv-accent"
          />
          <span>Auto-tweet when I go LIVE</span>
          <span className="text-white/40 text-[10px]">(uses your X handle above)</span>
        </label>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          data-testid="social-links-save"
          className="bg-tv-accent hover:bg-yellow-400 text-black font-black text-xs uppercase tracking-wider px-4 py-2 rounded-full disabled:opacity-40"
        >
          {saving ? "Saving…" : "Save links"}
        </button>
      </div>
    </section>
  );
}

function DraftsCard() {
  const [drafts, setDrafts] = useState([]);
  const [composerDraft, setComposerDraft] = useState(null); // null | "new" | draft obj

  const load = () => api.get("/creator/story-drafts").then((r) => setDrafts(r.data?.drafts || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const del = async (d) => {
    if (!window.confirm(`Delete draft "${d.title || "untitled"}"?`)) return;
    try {
      await api.delete(`/creator/story-drafts/${d.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const publish = async (d) => {
    try {
      await api.post(`/creator/story-drafts/${d.id}/publish`);
      toast.success("Published to Story Bar");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <section data-testid="studio-drafts" className="mb-6 tv-glass rounded-2xl border border-white/10 p-5">
        <div className="flex items-center gap-2 mb-1">
          <FileEdit className="w-5 h-5 text-tv-accent" />
          <h2 className="font-display font-bold text-xl text-white">Story drafts</h2>
          <span className="ml-auto text-white/40 text-xs">{drafts.length}/50 saved</span>
        </div>
        <p className="text-white/60 text-sm mb-3">Batch your stories, tweak stickers, then drop them whenever the moment's right.</p>

        <div className="flex flex-wrap gap-2 mb-3">
          <button
            data-testid="drafts-new-btn"
            onClick={() => setComposerDraft("new")}
            className="inline-flex items-center gap-1.5 bg-tv-accent hover:bg-tv-accent/90 text-black font-bold text-sm px-4 py-2 rounded-full"
          ><PlusIcon className="w-4 h-4" /> New draft</button>
        </div>

        {drafts.length === 0 ? (
          <div className="text-white/50 text-sm" data-testid="drafts-empty">No drafts yet. Record an idea, save it here, post it tomorrow.</div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {drafts.map((d) => (
              <div key={d.id} data-testid={`draft-card-${d.id}`} className={`bg-black/40 border rounded-xl p-3 flex flex-col gap-2 ${d.paused ? "border-yellow-500/40" : "border-white/10"}`}>
                <div className="aspect-[9/16] max-h-44 bg-black/60 rounded overflow-hidden flex items-center justify-center relative">
                  {d.thumbnail_url ? (
                    <img src={d.thumbnail_url} alt="" className="w-full h-full object-cover" />
                  ) : d.video_url ? (
                    <video src={d.video_url} className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-white/30 text-xs">No media</div>
                  )}
                  {d.scheduled_for && (
                    <div className="absolute bottom-1 left-1 right-1 text-[9px] font-black uppercase tracking-wider bg-black/70 text-white px-1.5 py-0.5 rounded text-center">
                      {d.paused ? "⏸ Paused" : "📅 " + new Date(d.scheduled_for).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </div>
                  )}
                </div>
                <div className="text-white font-bold text-sm truncate" title={d.title}>{d.title || "Untitled"}</div>
                <div className="flex flex-wrap gap-1">
                  {d.interactive && (
                    <span className="text-[9px] uppercase font-black tracking-wider bg-fuchsia-500/25 text-fuchsia-300 px-1.5 py-0.5 rounded">{d.interactive.type}</span>
                  )}
                  {d.music && (
                    <span className="text-[9px] uppercase font-black tracking-wider bg-green-500/25 text-green-300 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5">
                      <Music2 className="w-2.5 h-2.5" /> music
                    </span>
                  )}
                  {d.audio_only && (
                    <span className="text-[9px] uppercase font-black tracking-wider bg-pink-500/25 text-pink-300 px-1.5 py-0.5 rounded">voice</span>
                  )}
                </div>
                <div className="flex gap-1 mt-auto">
                  {d.scheduled_for && (
                    <button
                      data-testid={`draft-pause-${d.id}`}
                      onClick={async () => {
                        try {
                          await api.patch(`/creator/story-drafts/${d.id}`, { paused: !d.paused });
                          toast.success(d.paused ? "Resumed" : "Paused");
                          load();
                        } catch (e) { toast.error(formatApiError(e)); }
                      }}
                      className={`flex-1 font-bold py-1.5 rounded-full text-xs ${d.paused ? "bg-yellow-400 hover:bg-yellow-300 text-black" : "bg-white/10 hover:bg-white/15 text-white"}`}
                    >{d.paused ? "Resume" : "Pause"}</button>
                  )}
                  <button
                    data-testid={`draft-edit-${d.id}`}
                    onClick={() => setComposerDraft(d)}
                    className="flex-1 bg-white/10 hover:bg-white/15 text-white font-bold py-1.5 rounded-full text-xs"
                  >Edit</button>
                  <button
                    data-testid={`draft-publish-${d.id}`}
                    onClick={() => publish(d)}
                    disabled={!d.video_url}
                    className="flex-1 bg-tv-accent disabled:opacity-40 hover:bg-tv-accent/90 text-black font-bold py-1.5 rounded-full text-xs"
                  >Publish</button>
                  <button
                    data-testid={`draft-delete-${d.id}`}
                    onClick={() => del(d)}
                    className="p-1.5 text-white/50 hover:text-red-400"
                  ><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      {composerDraft && (
        <StoryComposer
          draft={composerDraft === "new" ? null : composerDraft}
          onClose={() => setComposerDraft(null)}
          onPosted={() => { setComposerDraft(null); load(); }}
        />
      )}
    </>
  );
}

